import { Injectable } from '@angular/core';

import { TokenSchema } from './auth.model';
import {
  AuthResponse,
  BaseAuthRequestParams,
  MemberNumberAuthRequestParams,
  MembershipNumberAuthRequestParams,
  MultipleVehiclesAuthResponse,
  OdometerLookupVehicle,
  RapAuthResponse, SearchIdPhoneNumberParams,
  SearchInfoSigninParams,
  SearchSigninParams,
  SecureAuthRequestParams
} from './auth.types';
import { DrrHttpClient } from '../../http/drr-http-client';
import { HttpContext } from '@angular/common/http';
import { INCLUDE_AUTH_HEADERS } from '../../shared/services/http-context.tokens';
import { from, of, Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

export const DEFAULT_VENDOR = 'RSOWeb'

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  constructor(private http: DrrHttpClient) {
  }

  secureSignin$({
    captchaVersion,
    captchaToken,
    memId,
    ets,
    vendor = DEFAULT_VENDOR,
  }: BaseAuthRequestParams & SecureAuthRequestParams): Observable<AuthResponse> {
    return this.http.post<AuthResponse>('bff/member/verification', {
      captchaVersion,
      captchaToken,
      memberNumber: memId,
      ets,
      vendor,
    }, {
      context: new HttpContext().set(INCLUDE_AUTH_HEADERS, false),
    }).pipe(
      switchMap((response) => from(TokenSchema.validate(response)))
    )

  }

  secureMembershipNumberSignin$({
    memberNumber,
  }: MembershipNumberAuthRequestParams): Observable<AuthResponse> {
    return this.http.post<AuthResponse>('bff/member/verification/partner', {
      memberNumber,
    }, {
      context: new HttpContext().set(INCLUDE_AUTH_HEADERS, false),
    }).pipe(
      switchMap((response) => from(TokenSchema.validate(response)))
    )
  }

  unsecureSignin$({
    captchaVersion,
    captchaToken,
    memberNumber,
    zipCode,
  }: BaseAuthRequestParams & MemberNumberAuthRequestParams): Observable<AuthResponse> {
    return this.http.post<AuthResponse>('bff/member/validation', {
      captchaVersion,
      captchaToken,
      memberNumber,
      zipCode,
    }, {
      context: new HttpContext().set(INCLUDE_AUTH_HEADERS, false),
    }).pipe(
      switchMap((response) => from(TokenSchema.validate(response)))
    )
  }

  searchSignin$({ searchId, resultId }: SearchSigninParams): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`bff/member/search/${searchId}/${resultId}`, {
      searchId,
      resultId,
    }, {
      context: new HttpContext().set(INCLUDE_AUTH_HEADERS, false),
    }).pipe(
      switchMap((response) => from(TokenSchema.validate(response)))
    )
  }

  searchSigninInfo$({ searchId, streetNumber, firstName, lastName }: SearchInfoSigninParams): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`bff/member/searchByInfo/${searchId}`, {
      streetNumber,
      firstName,
      lastName,
    }, {
      context: new HttpContext().set(INCLUDE_AUTH_HEADERS, false),
    }).pipe(
      switchMap((response) => from(TokenSchema.validate(response)))
    )
  }

  selectBySearchIdAndPhoneNumber$({ searchId, phoneNumber }: SearchIdPhoneNumberParams): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`bff/member/selectByPhoneNumber`, {
      searchId,
      phoneNumber,
    }, {
      context: new HttpContext().set(INCLUDE_AUTH_HEADERS, false),
    }).pipe(
      switchMap((response) => from(TokenSchema.validate(response)))
    )
  }

  oemValidation$(
    captchaVersion: string,
    captchaToken: string,
    vin: string,
    mileage: number,
    manufacturer: string,
    referenceId: string,
    country: string,
  ): Observable<RapAuthResponse> {
    return this.http.post<RapAuthResponse>('/rap/validation/oem', {
      captchaVersion,
      captchaToken,
      vin,
      odometer: mileage,
      manufacturer,
      referenceId,
      ...(country ? { country } : {}),
    }, {
      context: new HttpContext().set(INCLUDE_AUTH_HEADERS, false),
    })
  }

  plateToVinValidation$(
    captchaVersion: string,
    captchaToken: string,
    licensePlate: string,
    state: string,
    mileage: number,
    manufacturer: string,
    referenceId: string,
    country: string,
  ): Observable<RapAuthResponse | MultipleVehiclesAuthResponse> {
    return this.http.post<RapAuthResponse | MultipleVehiclesAuthResponse>('/bff/rap/validation/plate', {
      captchaVersion,
      captchaToken,
      licensePlate,
      state,
      odometer: mileage,
      manufacturer,
      referenceId,
      ...(country ? { country } : {}),
    }, {
      context: new HttpContext().set(INCLUDE_AUTH_HEADERS, false),
    }).pipe(
      // A 409 carrying multiple vehicles is emitted as a success by the error interceptor.
      // It has no access token, so it must bypass TokenSchema.
      switchMap((response) => (response as MultipleVehiclesAuthResponse)?.vehicles
        ? of(response)
        : from(TokenSchema.validate(response)))
    )
  }

  rentalValidation$(
    captchaVersion,
    captchaToken,
    rentalAgreement: string,
    referenceId: string
  ): Observable<{ rentalValidation: AuthResponse, captchaToken: string }> {
    return this.http.post<AuthResponse>('/rap/validation/rental', {
      captchaVersion,
      captchaToken,
      rentalAgreement,
      referenceId
    }, {
      context: new HttpContext().set(INCLUDE_AUTH_HEADERS, false),
    }).pipe(
      map((rentalValidation) => ({ rentalValidation, captchaToken }))
    )
  }

  vasValidation$(
    captchaVersion,
    captchaToken,
    subscriberId: string,
    referenceId: string
  ): Observable<{ vasValidation: AuthResponse, captchaToken: string }> {
    return this.http.post<AuthResponse>('/rap/validation/vas', {
      captchaVersion,
      captchaToken,
      subscriberId,
      referenceId
    }, {
      context: new HttpContext().set(INCLUDE_AUTH_HEADERS, false),
    }).pipe(
      map((vasValidation) => ({ vasValidation, captchaToken }))
    )
  }

  getOdometerByVin(vin: string): Observable<{ odometer: number }> {
    return this.http.get<{ odometer: number }>(`bff/rap/vehicle/odometer/${vin}`, {
      context: new HttpContext().set(INCLUDE_AUTH_HEADERS, false),
    })
  }

  getOdometerByPlate(state: string, plate: string): Observable<OdometerLookupVehicle[]> {
    return this.http.get<OdometerLookupVehicle[]>(`bff/rap/vehicle/odometer/plate/${state}/${plate}`, {
      context: new HttpContext().set(INCLUDE_AUTH_HEADERS, false),
    })
  }

  rapVerification$(
    access_token,
    odometer?: number
  ): Observable<RapAuthResponse> {
    return this.http.post<RapAuthResponse>('/rap/verification', odometer !== undefined ? { odometer } : {}, {
      headers: {
        Authorization: `Bearer ${access_token}`
      },
      context: new HttpContext().set(INCLUDE_AUTH_HEADERS, false),
    })
  }


}
